import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Globe, TrendingUp, Clock, DollarSign, Users, Award, ArrowRight, CheckCircle2, Shield, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import ConversionHero from '@/components/conversion/ConversionHero';
import EarningsCalculator from '@/components/conversion/EarningsCalculator';
import SuccessStoryCard from '@/components/conversion/SuccessStoryCard';
import UrgencyBanner from '@/components/conversion/UrgencyBanner';
import { ROICalculator } from '@/components/conversion/ROICalculator';
import { SoloPracticeVsPlatformComparison } from '@/components/conversion/ComparisonTables';
import { CountdownTimer, PriceIncreaseWarning } from '@/components/conversion/UrgencyElements';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import MasterSEOController from '@/components/seo/MasterSEOController';

const ForTherapists = () => {
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguage();
  
  const benefits = [
    {
      icon: Globe,
      title: 'Global Client Base',
      description: 'Reach clients in 150+ countries and expand your practice internationally',
      stat: '150+ countries'
    },
    {
      icon: DollarSign,
      title: 'Higher Earnings',
      description: 'Keep 90% of your earnings. Our top therapists earn $5K-$15K monthly',
      stat: 'Only 10% fee'
    },
    {
      icon: Clock,
      title: 'Complete Flexibility',
      description: 'Set your own schedule. Work from anywhere, anytime',
      stat: '24/7 availability'
    },
    {
      icon: Users,
      title: 'Qualified Clients',
      description: 'Connect with clients who are serious about their mental health journey',
      stat: 'Pre-screened'
    },
    {
      icon: Shield,
      title: 'Secure Platform',
      description: 'HIPAA-compliant video sessions and encrypted communications',
      stat: 'Bank-level security'
    },
    {
      icon: Zap,
      title: 'Fast Onboarding',
      description: 'Get your first client within 7 days of approval',
      stat: '7-day avg'
    }
  ];
  
  const successStories = [
    {
      name: 'Dr. Maria González',
      role: 'Clinical Psychologist, Spain',
      story: 'I went from seeing 15 local clients per month to 45 international clients. The platform opened doors I never imagined. My income tripled in 6 months.',
      beforeStat: '$2,000/mo',
      afterStat: '$12,000/mo',
      highlight: '6x growth in 6 months'
    },
    {
      name: 'Dr. James Thompson',
      role: 'Psychiatrist, UK',
      story: 'Being able to reach clients globally transformed my practice. I now work flexible hours and earn more while helping people across continents.',
      beforeStat: '20 hrs/week',
      afterStat: '25 hrs/week',
      highlight: 'Earning 2.5x more'
    },
    {
      name: 'Dr. Sofia Rossi',
      role: 'Psychotherapist, Italy',
      story: 'The platform gave me financial freedom. I work when I want, with clients who truly value my expertise. Best career decision ever.',
      beforeStat: '$3,500/mo',
      afterStat: '$9,200/mo',
      highlight: '163% increase'
    }
  ];
  
  const requirements = [
    {
      title: 'Master in Brief Psychotherapy',
      description: 'Completion of Dr. José Luis Marín\'s Master program',
      link: 'https://formacionpsicoterapia.com'
    },
    {
      title: 'Valid Professional License',
      description: 'Current license in your country of practice'
    },
    {
      title: 'Clinical Experience',
      description: 'Minimum 2 years of professional practice'
    },
    {
      title: 'Professional Insurance',
      description: 'Valid liability insurance coverage'
    },
    {
      title: 'Technology Setup',
      description: 'Reliable internet and video conferencing capability'
    }
  ];
  
  const howItWorks = [
    {
      step: 1,
      title: 'Apply in Minutes',
      description: 'Complete our simple application form with your credentials and experience',
      duration: '5 minutes'
    },
    {
      step: 2,
      title: 'Verification Process',
      description: 'Our team verifies your qualifications and Master completion',
      duration: '2-3 days'
    },
    {
      step: 3,
      title: 'Profile Setup',
      description: 'Create your professional profile, set rates, and availability',
      duration: '30 minutes'
    },
    {
      step: 4,
      title: 'Start Earning',
      description: 'Get matched with clients and begin sessions',
      duration: 'Within 7 days'
    }
  ];
  
  const faqs = [
    {
      question: 'What is the commission structure?',
      answer: 'We charge only 10% commission per session. You keep 90% of everything you earn. There are no monthly fees, hidden charges, or subscription costs.'
    },
    {
      question: 'How quickly can I start seeing clients?',
      answer: 'Once approved, most therapists get their first client within 7 days. Some start even sooner. It depends on your availability and specialty.'
    },
    {
      question: 'Can I set my own rates?',
      answer: 'Absolutely! You have complete control over your session rates. We recommend competitive pricing based on your region and experience, but the final decision is yours.'
    },
    {
      question: 'Is the Master in Brief Psychotherapy required?',
      answer: 'Yes, it\'s a mandatory requirement. This ensures all therapists on our platform maintain the highest quality standards set by Dr. José Luis Marín.'
    },
    {
      question: 'Can I work part-time or keep my current practice?',
      answer: 'Yes! Many therapists use our platform to supplement their existing practice. You control your availability completely.'
    },
    {
      question: 'What technology do I need?',
      answer: 'You need a reliable internet connection, a computer or tablet with a webcam, and a quiet private space for sessions. Our platform handles all the video conferencing.'
    }
  ];
  
  return (
    <>
      <MasterSEOController
        pageType="static"
        currentLanguage="en"
        customMetaTags={{
          title: "Join as a Therapist | Earn $5K-$15K/Month | Global Therapy Connect",
          description: "Join 5,000+ therapists earning globally. Only 10% commission. Flexible schedule. Reach clients in 150+ countries. Get your first client in 7 days.",
          keywords: ['therapist jobs', 'online therapy jobs', 'psychologist opportunities', 'work from home therapist', 'global mental health practice', 'telehealth therapist']
        }}
        enabledFeatures={{
          multilingual: true,
          trustSignals: true
        }}
      />
      
      {/* Hero with Calculator */}
      <ConversionHero
        headline="Turn Your Expertise Into Global Impact"
        subheadline="Join 5,000+ therapists earning $5K-$15K/month by reaching clients worldwide. Set your schedule. Keep 90% of earnings."
        primaryCTA={{
          text: "Start Application",
          onClick: () => navigate(getLocalizedPath('/login?tab=signup'))
        }}
        secondaryCTA={{
          text: "Calculate Earnings",
          onClick: () => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' }),
          variant: 'outline'
        }}
        trustBadges={[
          '5,000+ Active Therapists',
          'Only 10% Commission',
          'First Client in 7 Days'
        ]}
        rightContent={
          <div id="calculator">
            <EarningsCalculator onCTA={() => navigate(getLocalizedPath('/login?tab=signup'))} />
          </div>
        }
      />
      
      {/* Urgency Elements - Sprint 3 */}
      <div className="container mx-auto px-4 -mt-8 mb-8">
        <PriceIncreaseWarning daysLeft={7} />
      </div>
      
      {/* Urgency Banner */}
      <UrgencyBanner 
        message="127 therapists joined this week • Limited spots available this month"
        type="info"
        className="container mx-auto px-4 mb-16"
      />
      
      {/* Success Stories */}
      <section className="py-16 bg-accent/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4">Success Stories</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Real Therapists, Real Results
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how therapists like you transformed their practice and income
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {successStories.map((story, index) => (
              <SuccessStoryCard key={index} {...story} />
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Therapists Choose Our Platform
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to build a thriving global practice
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <Badge variant="secondary" className="mb-3">{benefit.stat}</Badge>
                    <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* ROI Calculator for Therapists - Sprint 3 */}
      <div className="py-8">
        <ROICalculator />
      </div>
      
      {/* Solo Practice vs Platform Comparison - Sprint 3 */}
      <SoloPracticeVsPlatformComparison />
      
      {/* How It Works */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How It Works
            </h2>
            <p className="text-lg text-muted-foreground">
              Get started in 4 simple steps
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              {howItWorks.map((step, index) => (
                <div key={index} className="relative">
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-border" />
                  )}
                  <div className="relative bg-card border rounded-lg p-6 hover:shadow-lg transition-all">
                    <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold mb-4 mx-auto">
                      {step.step}
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-center">{step.title}</h3>
                    <p className="text-sm text-muted-foreground text-center mb-3">
                      {step.description}
                    </p>
                    <Badge variant="outline" className="w-full justify-center">
                      {step.duration}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Requirements */}
      <section className="py-16 bg-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Requirements
              </h2>
              <p className="text-lg text-muted-foreground">
                Most qualified therapists easily meet these criteria
              </p>
            </div>
            
            <Card>
              <CardContent className="p-8">
                <div className="space-y-6">
                  {requirements.map((req, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg mb-1">{req.title}</h3>
                        <p className="text-muted-foreground">{req.description}</p>
                        {req.link && (
                          <a 
                            href={req.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline inline-flex items-center gap-1 mt-1"
                          >
                            Learn more <ArrowRight className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 text-center">
                    <strong>Not sure if you qualify?</strong> Our approval rate is 90% for qualified applicants. Apply now and we'll guide you through the process.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* Pricing Transparency */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-lg text-muted-foreground">
                We only win when you win
              </p>
            </div>
            
            <Card className="border-2 border-primary">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="text-5xl font-bold text-primary mb-2">10%</div>
                  <p className="text-xl text-muted-foreground">Commission per session</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    You keep <strong className="text-foreground">90%</strong> of everything you earn
                  </p>
                </div>
                
                <div className="bg-accent/20 rounded-lg p-6 mb-6">
                  <h3 className="font-semibold mb-4 text-center">Platform vs Solo Practice</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3 text-green-600">Our Platform</h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>10% commission only</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>No monthly fees</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>Global marketing included</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>Payment processing included</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-3 text-red-600">Solo Practice</h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <span>• Website hosting: $20-50/mo</span>
                        </li>
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <span>• Marketing: $200-500/mo</span>
                        </li>
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <span>• Payment processor: 3-5%</span>
                        </li>
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <span>• Video software: $30-100/mo</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    Net earnings example: $100 session = <strong className="text-primary text-lg">$90 to you</strong>
                  </p>
                  <Button 
                    size="lg"
                    onClick={() => navigate(getLocalizedPath('/login?tab=signup'))}
                    className="text-lg px-8"
                  >
                    Start Application Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      
      {/* FAQs */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-muted-foreground">
                Everything therapists ask before joining
              </p>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      
      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-primary/20 to-accent/20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Practice?
            </h2>
            <p className="text-lg text-muted-foreground mb-2">
              Join thousands of therapists already serving clients worldwide
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              90% approval rate • Average first client in 7 days • No monthly fees
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate(getLocalizedPath('/login?tab=signup'))}
                className="text-lg px-8 py-6 group"
              >
                Start Your Application
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-lg px-8 py-6"
              >
                Calculate Your Earnings
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-6">
              Application takes 2-3 minutes • No credit card required
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default ForTherapists;
